import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from 'cms/core/shared/AdminLayout';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    Avatar,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { UserHttpService } from 'core/services/user';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';
import { Utils } from 'core/utils';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';

function UserDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);

    const { data: user, isLoading, error } = useQuery(
        ['admin_user_detail', id],
        async () => {
            return await UserHttpService.loadUser(id);
        },
        { enabled: !!id }
    );

    const updateMutation = useMutation(
        async (data) => {
            return await UserHttpService.updateUser(id, data);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['admin_user_detail', id]);
                queryClient.invalidateQueries('users');
                enqueueSnackbar('User updated successfully', { variant: 'success' });
                setIsEditing(false);
            },
            onError: (error) => {
                enqueueSnackbar('Failed to update user', { variant: 'error' });
                console.error('Update error:', error);
            }
        }
    );

    const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            is_active: true,
            is_verified: false,
            role: 'employee',
            phone_number: '',
            city: '',
            country_id: '',
            gender: '',
            birth_date: '',
            home_address: '',
            postalcode: '',
            about: '',
            website: '',
            facebook: '',
            instagram: '',
            linkedin: '',
            ready_to_work: true
        }
    });

    React.useEffect(() => {
        if (user) {
            reset({
                first_name: user.profile?.first_name || '',
                last_name: user.profile?.last_name || '',
                email: user.email || '',
                is_active: user.is_active !== undefined ? user.is_active : true,
                is_verified: user.is_verified !== undefined ? user.is_verified : false,
                role: user.profile?.role || 'employee',
                phone_number: user.profile?.phone_number || '',
                city: user.profile?.city || '',
                country_id: user.profile?.country_id || '',
                gender: user.profile?.gender || '',
                birth_date: user.profile?.birth_date || '',
                home_address: user.profile?.home_address || '',
                postalcode: user.profile?.postalcode || '',
                about: user.profile?.about || '',
                website: user.profile?.website || '',
                facebook: user.profile?.facebook || '',
                instagram: user.profile?.instagram || '',
                linkedin: user.profile?.linkedin || '',
                ready_to_work: user.profile?.ready_to_work !== undefined ? user.profile?.ready_to_work : true
            });
        }
    }, [user, reset]);

    const onSubmit = async (data) => {
        updateMutation.mutate(data);
    };

    if (isLoading) {
        return (
            <AdminLayout pageTitle="User Details">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    if (error || !user) {
        return (
            <AdminLayout pageTitle="User Details">
                <Alert severity="error">Failed to load user details</Alert>
            </AdminLayout>
        );
    }

    const profile = user.profile || {};
    const displayName = getProfileDisplayName(profile, user);
    const avatarSrc = getProfileAvatarUrl(profile);

    return (
        <AdminLayout pageTitle="User Details">
            <Box>
                {/* Header */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <IconButton onClick={() => navigate('/cms/user')} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>
                        User Details
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {!isEditing && (
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => setIsEditing(true)}
                        >
                            Edit User
                        </Button>
                    )}
                </Stack>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                        {/* Left Column - Profile Info */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3 }}>
                                <Stack spacing={3} alignItems="center">
                                    <Avatar
                                        src={avatarSrc || undefined}
                                        alt={displayName}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            fontSize: '2.5rem',
                                            fontWeight: 700,
                                            bgcolor: nameToAvatarColor(displayName),
                                        }}
                                    >
                                        {getProfileAvatarLetter(profile, user)}
                                    </Avatar>
                                    
                                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                                        <Typography variant="h5" fontWeight={600} gutterBottom>
                                            {profile.first_name || ''} {profile.last_name || ''}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {user.email}
                                        </Typography>
                                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                            {user.is_verified ? (
                                                <Chip label="Approved" color="success" size="small" />
                                            ) : (
                                                <Chip label="Pending" color="warning" size="small" />
                                            )}
                                            {user.is_active ? (
                                                <Chip label="Active" color="success" size="small" />
                                            ) : (
                                                <Chip label="Inactive" color="error" size="small" />
                                            )}
                                        </Stack>
                                    </Box>

                                    <Divider sx={{ width: '100%' }} />

                                    {/* Quick Actions */}
                                    {isEditing && (
                                        <Box sx={{ width: '100%' }}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Quick Actions
                                            </Typography>
                                            <Stack spacing={2}>
                                                <FormControlLabel
                                                    control={
                                                        <Controller
                                                            name="is_verified"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Switch
                                                                    checked={field.value}
                                                                    onChange={field.onChange}
                                                                    color="success"
                                                                />
                                                            )}
                                                        />
                                                    }
                                                    label="Approve User"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Controller
                                                            name="is_active"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Switch
                                                                    checked={field.value}
                                                                    onChange={field.onChange}
                                                                    color="primary"
                                                                />
                                                            )}
                                                        />
                                                    }
                                                    label="Active Status"
                                                />
                                            </Stack>
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* Right Column - Form Fields */}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <Typography variant="h6" fontWeight={600}>
                                        User Information
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="First Name"
                                                {...register('first_name', { required: 'First name is required' })}
                                                error={!!errors.first_name}
                                                helperText={errors.first_name?.message}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Last Name"
                                                {...register('last_name', { required: 'Last name is required' })}
                                                error={!!errors.last_name}
                                                helperText={errors.last_name?.message}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Email"
                                                type="email"
                                                {...register('email', { required: 'Email is required' })}
                                                error={!!errors.email}
                                                helperText={errors.email?.message}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth disabled={!isEditing}>
                                                <InputLabel>Role</InputLabel>
                                                <Controller
                                                    name="role"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select {...field} label="Role">
                                                            <MenuItem value="employee">Employee</MenuItem>
                                                            <MenuItem value="employer">Employer</MenuItem>
                                                            <MenuItem value="admin">Admin</MenuItem>
                                                            <MenuItem value="editor">Editor</MenuItem>
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Phone Number"
                                                {...register('phone_number')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="City"
                                                {...register('city')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth disabled={!isEditing}>
                                                <InputLabel>Gender</InputLabel>
                                                <Controller
                                                    name="gender"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select {...field} label="Gender">
                                                            <MenuItem value="">None</MenuItem>
                                                            <MenuItem value={1}>Male</MenuItem>
                                                            <MenuItem value={2}>Female</MenuItem>
                                                            <MenuItem value={3}>Other</MenuItem>
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Birth Date"
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                                {...register('birth_date')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Home Address"
                                                multiline
                                                rows={2}
                                                {...register('home_address')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Postal Code"
                                                {...register('postalcode')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="About"
                                                multiline
                                                rows={4}
                                                {...register('about')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider />

                                    <Typography variant="h6" fontWeight={600}>
                                        Social Links
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Website"
                                                {...register('website')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Facebook"
                                                {...register('facebook')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Instagram"
                                                {...register('instagram')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="LinkedIn"
                                                {...register('linkedin')}
                                                disabled={!isEditing}
                                            />
                                        </Grid>
                                    </Grid>

                                    {isEditing && (
                                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => {
                                                    setIsEditing(false);
                                                    reset();
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                disabled={updateMutation.isLoading}
                                            >
                                                {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </AdminLayout>
    );
}

export default UserDetailsPage;

